/**
 * End-to-end public map share links (/m/:layerId).
 * - Guests: snapshot workspace → sign-in → resume publish
 * - Signed-in: publish layer to API and return shareable URL
 */

import { apiJson } from './apiClient';
import {
  layersToGuestPayload,
  saveGuestLayersSnapshot,
  claimGuestWorkspace,
  type ClaimedGuestLayer,
} from './guestWorkspaceService';
import { trackEvent } from './analyticsService';

const PENDING_SHARE_KEY = 'geovera_pending_share_v1';

export type ShareLayerInput = {
  id?: string;
  name?: string;
  type?: string;
  geometryType?: string;
  crs?: string;
  symbology?: Record<string, unknown> | null;
  features?: Array<{
    geometry?: { type: string; coordinates: unknown };
    properties?: Record<string, unknown>;
  }>;
};

export type PendingShareIntent = {
  intent: 'share';
  returnPath: string;
  preferredLayerId?: string;
  preferredLayerName?: string;
  savedAt: string;
};

export type PublishShareResult = {
  success: boolean;
  layerId?: string;
  sharePath?: string;
  shareUrl?: string;
  created?: boolean;
  featureCount?: number;
  message?: string;
  needsAuth?: boolean;
};

function isUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

function isDemoLayerId(id?: string): boolean {
  if (!id) return true;
  return id.startsWith('demo-') || id === 'demo-sample-parcel-101';
}

/** Absolute share URL for the current origin. */
export function buildShareUrl(layerId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://geovera.in';
  return `${origin}/m/${layerId}`;
}

/** Login redirect that resumes share on the map workspace. */
export function getShareLoginRedirect(returnPath = '/map'): string {
  const base = returnPath.startsWith('/') ? returnPath.split('?')[0] : '/map';
  // Prefer free map route for guests; workspace also works
  const path = base === '/dashboard' || base === '/map' || base.startsWith('/map') ? '/map' : base;
  return `${path}?share=1`;
}

export function setPendingShareIntent(partial?: Partial<PendingShareIntent>): void {
  const payload: PendingShareIntent = {
    intent: 'share',
    returnPath: partial?.returnPath || '/map',
    preferredLayerId: partial?.preferredLayerId,
    preferredLayerName: partial?.preferredLayerName,
    savedAt: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(PENDING_SHARE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function peekPendingShareIntent(): PendingShareIntent | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SHARE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingShareIntent;
    if (parsed?.intent !== 'share') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingShareIntent(): void {
  try {
    sessionStorage.removeItem(PENDING_SHARE_KEY);
  } catch {
    /* ignore */
  }
}

export function consumePendingShareIntent(): PendingShareIntent | null {
  const p = peekPendingShareIntent();
  if (p) clearPendingShareIntent();
  return p;
}

/** Rewrite a pending guest layer ID to the cloud ID returned by claim. */
export function remapPendingShareLayer(claimedLayers: ClaimedGuestLayer[]): void {
  const pending = peekPendingShareIntent();
  if (!pending?.preferredLayerId) return;
  const claimed = claimedLayers.find((layer) => layer.sourceLayerId === pending.preferredLayerId);
  if (!claimed) return;
  setPendingShareIntent({
    ...pending,
    preferredLayerId: claimed.id,
    preferredLayerName: claimed.name || pending.preferredLayerName,
  });
}

function getShareableLayers(layers: ShareLayerInput[]): ShareLayerInput[] {
  return layers.filter(
    (layer) =>
      (layer.type === 'vector' || !layer.type || layer.geometryType) &&
      layer.geometryType &&
      ['Point', 'LineString', 'Polygon'].includes(layer.geometryType)
  );
}

/**
 * Resolve an exact preferred vector layer, or the sole eligible layer when unambiguous.
 */
export function pickShareLayer(
  layers: ShareLayerInput[],
  preferredId?: string
): ShareLayerInput | null {
  const vectors = getShareableLayers(layers);
  if (preferredId) {
    return vectors.find((layer) => layer.id === preferredId) || null;
  }
  return vectors.length === 1 ? vectors[0] : null;
}

/**
 * Persist guest layers + pending intent, then return login path.
 */
export function prepareShareAuthRedirect(
  layers: ShareLayerInput[],
  returnPath = '/map',
  preferredLayerId?: string
): { loginPath: string } {
  const payload = layersToGuestPayload(
    layers.map((l) => ({
      id: l.id,
      name: l.name,
      geometryType: l.geometryType,
      crs: l.crs,
      symbology: l.symbology ?? null,
      features: l.features,
    }))
  );
  if (payload.length) {
    saveGuestLayersSnapshot(payload);
  }

  const chosen = pickShareLayer(layers, preferredLayerId);
  setPendingShareIntent({
    returnPath,
    preferredLayerId: chosen?.id || preferredLayerId,
    preferredLayerName: chosen?.name,
  });

  trackEvent('share_auth_required', {
    hasLayers: layers.length,
    hasFeatures: !!chosen?.features?.length,
  });

  const redirect = getShareLoginRedirect(returnPath);
  return {
    loginPath: `/login?redirect=${encodeURIComponent(redirect)}&message=${encodeURIComponent(
      'Sign in free to create your share link. Your map will be waiting.'
    )}`,
  };
}

/**
 * Publish share link for authenticated user.
 * Claims guest snapshot first, then publish-share API.
 */
export async function publishShareLink(
  layers: ShareLayerInput[],
  opts?: { preferredLayerId?: string; skipClaim?: boolean }
): Promise<PublishShareResult> {
  if (!opts?.skipClaim) {
    try {
      const claim = await claimGuestWorkspace();
      if (claim.success) remapPendingShareLayer(claim.layers);
    } catch {
      /* claim optional */
    }
  }

  const pending = peekPendingShareIntent();
  const preferredId = opts?.preferredLayerId || pending?.preferredLayerId;
  const layer = pickShareLayer(layers, preferredId);

  if (!layer) {
    const shareableCount = getShareableLayers(layers).length;
    return {
      success: false,
      message: preferredId
        ? 'The selected layer is no longer available. Select that layer again and retry.'
        : shareableCount > 1
          ? 'Select a single layer to share, then try again.'
          : 'No shareable vector layer is available. Draw or import a layer first.',
    };
  }

  // Prefer server UUID if layer already cloud-backed
  const existingId =
    layer?.id && isUuid(layer.id) && !isDemoLayerId(layer.id) ? layer.id : undefined;

  const features =
    layer?.features
      ?.filter((f) => f.geometry?.type)
      .slice(0, 500)
      .map((f) => ({
        geometry: f.geometry as { type: string; coordinates: unknown },
        properties: f.properties || {},
      })) || [];

  // If no features on preferred layer but we have guest-style data, still try create
  const body: Record<string, unknown> = {
    layerId: existingId,
    name: layer?.name || pending?.preferredLayerName || 'Shared map',
    geometryType: layer?.geometryType || features[0]?.geometry?.type || 'Polygon',
    crs: layer?.crs || 'EPSG:4326',
    symbology: layer?.symbology ?? undefined,
    features,
  };

  try {
    const data = await apiJson<{
      success: boolean;
      layerId?: string;
      sharePath?: string;
      created?: boolean;
      featureCount?: number;
      message?: string;
      error?: string;
    }>('/api/layers/publish-share', {
      method: 'POST',
      body,
      retry: false,
    });

    if (!data.success || !data.layerId) {
      return {
        success: false,
        message: data.message || data.error || 'Failed to create share link',
      };
    }

    clearPendingShareIntent();
    const shareUrl = buildShareUrl(data.layerId);
    trackEvent('share_created', {
      layerId: data.layerId,
      created: !!data.created,
      featureCount: data.featureCount,
    });

    return {
      success: true,
      layerId: data.layerId,
      sharePath: data.sharePath || `/m/${data.layerId}`,
      shareUrl,
      created: data.created,
      featureCount: data.featureCount,
      message: data.message,
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Failed to create share link';
    // Auth errors
    if (/401|unauthorized|sign\s*in|not authenticated/i.test(msg)) {
      return { success: false, needsAuth: true, message: msg };
    }
    if (/profile incomplete|complete your profile/i.test(msg)) {
      return {
        success: false,
        message: 'Complete your profile first, then open Share again to get your link.',
      };
    }
    return { success: false, message: msg };
  }
}

/**
 * Copy text to clipboard with fallback.
 */
export async function copyShareUrl(url: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
